import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import axiosInstance from "../../config/axios-config";
import {initialReviewDraft, reviewProposal, reviewValidation} from "./order-review-model";

export default function useOrderReview({open, lineId, onSubmitted}) {
    const {tenantId, tenantType, userIdentity, userRole} = useUser();
    const {i18n} = useTranslation();
    const scope = `${tenantId}:${userIdentity}:${userRole}:${tenantType}:${i18n.language}:${lineId}:${open}`;
    const permitted = Boolean(tenantId && userIdentity) && tenantType === "IMPORTER" && ["ADMIN", "OPERATOR", "SYSTEM"].includes(userRole);
    const current = useRef(scope); current.current = scope;
    const read = useRef(null), write = useRef(null), attempt = useRef(null);
    const [state, setState] = useState({scope, loading: true});
    const [preview, setPreview] = useState(null);
    const value = state.scope === scope ? state : {scope, loading: true};
    const headers = {"Language-Context": i18n.language.toUpperCase()};

    const load = async (preserve = false) => {
        if (write.current) return;
        read.current?.abort();
        const controller = new AbortController(); read.current = controller;
        setState(old => ({...(old.scope === scope ? old : {}), scope, loading: true, error: null}));
        try {
            const {data} = await axiosInstance.get(`/api/v1/supplier-orders/by-line/${lineId}/review`, {signal: controller.signal, headers});
            if (current.current !== scope || controller.signal.aborted) return;
            attempt.current = null;
            // A submitted proposal must display saved values, including after a conflict refresh from another session.
            const keepDraft = preserve && data.editable;
            setState(old => ({scope, review: data, draft: initialReviewDraft(data, keepDraft ? old.draft : {}),
                delivery: keepDraft && old.delivery ? old.delivery : data.order.deliveryCommitment?.slice(0, 16) || `${data.serverDate}T12:00`, loading: false}));
        } catch (error) {
            if (current.current === scope && !controller.signal.aborted) setState(old => ({...old, scope, loading: false, conflict: true,
                error: error.response?.status === 404 ? "ReviewLegacyUnavailable" : "ReviewLoadFailed"}));
        }
    };

    useEffect(() => {
        attempt.current = null; setPreview(null); setState({scope, loading: true});
        if (open && permitted && lineId) load();
        return () => { read.current?.abort(); write.current?.controller.abort(); write.current = null; };
        // Identity, language and navigation define a new review; retaining another scope's draft is unsafe.
    }, [scope, permitted]);

    const errors = value.review ? reviewValidation(value.review, value.draft, value.delivery) : {};
    const proposal = value.review ? reviewProposal(value.review, value.draft, value.delivery) : null;
    const fingerprint = proposal ? JSON.stringify(proposal) : "";
    useEffect(() => {
        if (!open || !value.review?.editable || value.loading || value.conflict || Object.keys(errors).length || value.pending || value.receipt || value.uncertain) return;
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setPreview({scope, fingerprint, loading: true});
            try {
                const {data} = await axiosInstance.post(`/api/v1/supplier-orders/${value.review.order.supplierOrderId}/approvals/approval-preview`,
                    proposal, {signal: controller.signal, headers, timeout: 15000});
                if (current.current === scope && !controller.signal.aborted) setPreview({scope, fingerprint, summary: data});
            } catch (error) {
                if (current.current === scope && !controller.signal.aborted) {
                    setPreview({scope, fingerprint, error: true});
                    if (error.response?.status === 409) setState(old => ({...old, conflict: true, error: "ReviewConflict"}));
                }
            }
        }, 350);
        return () => { clearTimeout(timer); controller.abort(); };
        // Proposal fingerprint includes every editable field and every server row revision.
    }, [scope, fingerprint, value.loading, value.conflict, value.pending, Boolean(value.receipt), value.uncertain]);

    const update = changes => {
        if (write.current || value.uncertain || value.receipt || !value.review?.editable) return;
        attempt.current = null;
        setState(old => ({...old, ...changes, error: old.conflict ? old.error : null}));
    };
    const calculateAgain = () => { setPreview(null); load(true); };
    const currentPreview = preview?.scope === scope && preview.fingerprint === fingerprint ? preview : null;
    const canSubmit = permitted && value.review?.editable && !value.loading && !value.conflict && !value.receipt
        && !Object.keys(errors).length && Boolean(currentPreview?.summary);

    const submit = async () => {
        if (!canSubmit || write.current) return;
        const operation = {controller: new AbortController(), scope}; write.current = operation;
        // Keep the same key and exact body for a deliberate retry after an ambiguous response.
        const body = attempt.current?.fingerprint === fingerprint ? attempt.current.body : {requestId: crypto.randomUUID(), proposal};
        attempt.current = {fingerprint, body};
        setState(old => ({...old, pending: true, error: null}));
        try {
            const {data} = await axiosInstance.post(`/api/v1/supplier-orders/${value.review.order.supplierOrderId}/approval-proposals`,
                // A timeout leaves the server outcome unknown; keep the retry key/body in the existing uncertain state.
                body, {signal: operation.controller.signal, headers, timeout: 15000});
            if (current.current !== scope || operation.controller.signal.aborted) return;
            setState(old => ({...old, pending: false, uncertain: false, receipt: data}));
            onSubmitted?.(data);
        } catch (error) {
            if (current.current !== scope || operation.controller.signal.aborted) return;
            const status = error.response?.status;
            setState(old => ({...old, pending: false, conflict: status === 409, uncertain: !status || status >= 500,
                error: status === 409 ? "ReviewConflict" : !status || status >= 500 ? "ReviewUncertain" : "ReviewSubmitFailed"}));
        } finally { if (write.current === operation) write.current = null; }
    };
    return {...value, permitted, errors, preview: currentPreview, canSubmit, update, submit, refresh: calculateAgain};
}
